import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetApiData } from '@grc/ui-utility';
import { GetMyWorkItemsDetails } from '../../../service/api/get-my-work-items-details-service';
import { IShipmentGridRow } from '../../../models/shipments/shipment-grid-row.interface';
import { IDateRangeFilterValue } from '../../../common/date-range-filter/date-range-filter.interface';
import { toApiDateRange } from '../../../common/date-range-filter/date-range-filter.utils';
import { homeShipmentsAccountId } from '../../../common/constants/constants';
import { MyWorkItemsDetailsTab } from '../../../models/home/work-item.enum';

// A single fetch at the server's max page size (no server-side ag-Grid pagination exists in
// this app yet) - the grid's own client-side isPagination slices this in-browser. Matches
// Order.Service's Constants.MaxPageSize.
const DETAILS_PAGE_SIZE = 200;

export function useWorkItemDetails(
  tab: MyWorkItemsDetailsTab,
  dateFilter: IDateRangeFilterValue,
  searchText = ''
) {
  const { fetchData } = useGetApiData();
  const [rows, setRows] = useState<IShipmentGridRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadRows = useCallback(async (): Promise<IShipmentGridRow[]> => {
    const params = {
      accountId: homeShipmentsAccountId,
      tab,
      ...toApiDateRange(dateFilter),
      page: 1,
      pageSize: DETAILS_PAGE_SIZE
    };
    return GetMyWorkItemsDetails(fetchData, params);
  }, [fetchData, tab, dateFilter]);

  useEffect(() => {
    let isCancelled = false;

    const load = async () => {
      setIsLoading(true);
      const result = await loadRows();
      if (!isCancelled) {
        setRows(result);
        setIsLoading(false);
      }
    };

    load();

    return () => {
      isCancelled = true;
    };
  }, [loadRows]);

  // MyWorkItemsDetails has no search parameter - filtering happens client-side on the
  // already-fetched page instead of refetching per keystroke.
  const filteredRows = useMemo(() => {
    const trimmed = searchText.trim().toLowerCase();
    if (!trimmed) return rows;
    return rows.filter(row =>
      row.shipmentNumber?.toLowerCase().includes(trimmed)
    );
  }, [rows, searchText]);

  return { rows: filteredRows, isLoading };
}

export default useWorkItemDetails;
